/**
 * 全局常量定义
 */

/** HTTP Header 名称 */
export const HEADER = {
  /** 内部服务绑定调用标识（Worker 间互调） */
  INTERNAL_CALL: 'X-Internal-Call',
  /** 内部调用签名（HMAC-SHA256） */
  INTERNAL_SIGNATURE: 'X-Internal-Signature',
  /** 内部调用时间戳（防重放） */
  INTERNAL_TIMESTAMP: 'X-Internal-Timestamp',
  /** 客户端请求签名（HMAC-SHA256） */
  CLIENT_SIGNATURE: 'X-Signature',
  /** 客户端请求时间戳 */
  CLIENT_TIMESTAMP: 'X-Timestamp',
  /** 应用 ID（verify-worker 客户端调用） */
  APP_ID: 'X-App-Id',
  /** Authorization header */
  AUTHORIZATION: 'Authorization',
  /** Bearer 前缀 */
  BEARER_PREFIX: 'Bearer ',
} as const;

/** JWT 类型 */
export const JWT_TYPE = {
  ACCESS: 'access',
  REFRESH: 'refresh',
} as const;

/** Token 有效期（秒） */
export const TOKEN_TTL = {
  /** Access token：15 分钟 */
  ACCESS: 15 * 60,
  /** Refresh token：30 天 */
  REFRESH: 30 * 24 * 60 * 60,
} as const;

/** KV Key 前缀与模板 */
export const KV_KEY = {
  /** refresh token → user_id，TTL=TOKEN_TTL.REFRESH */
  SESSION: (refreshToken: string) => `session:${refreshToken}`,
  /** access token jti 黑名单，TTL=TOKEN_TTL.ACCESS */
  BLACKLIST: (jti: string) => `blacklist:${jti}`,
  /** 在线设备会话 → {key_id, expire_at}，TTL=heartbeat_timeout */
  ONLINE: (appId: number, machineCode: string) => `online:${appId}:${machineCode}`,
  /** 应用在线设备数（计数器，无 TTL） */
  ONLINE_COUNT: (appId: number) => `online_count:${appId}`,
  /** IP 频率限制计数，TTL=60s */
  RATELIMIT: (ip: string, minute: string) => `ratelimit:${ip}:${minute}`,
  /** 验证结果短期缓存，TTL=5s */
  VERIFY_CACHE: (appId: number, key: string, machineCode: string) =>
    `verify_cache:${appId}:${key}:${machineCode}`,
  /** 冻结卡密标记（无 TTL，主动删除） */
  FROZEN_KEY: (appId: number, key: string) => `frozen_keys:${appId}:${key}`,
} as const;

/** 用户角色 */
export const ROLE = {
  ADMIN: 'admin',
  USER: 'user',
} as const;

/** 用户状态 */
export const USER_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;

/** 应用计费模式 */
export const BILLING_MODE = {
  /** 时长卡 */
  TIME: 'time',
  /** 次数卡 */
  COUNT: 'count',
  /** 永久卡 */
  FOREVER: 'forever',
} as const;

/** 卡密类型（与计费模式对齐） */
export const KEY_TYPE = BILLING_MODE;

/** 卡密状态 */
export const KEY_STATUS = {
  UNUSED: 'unused',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  FROZEN: 'frozen',
  USED_UP: 'used_up',
} as const;

/** 应用状态 */
export const APP_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;

/** 验证动作 */
export const VERIFY_ACTION = {
  VERIFY: 'verify',
  HEARTBEAT: 'heartbeat',
  LOGOUT: 'logout',
} as const;

/** 验证结果 */
export const VERIFY_RESULT = {
  SUCCESS: 'success',
  FAIL: 'fail',
} as const;

/** HTTP 方法 */
export const HTTP_METHOD = {
  GET: 'GET',
  POST: 'POST',
} as const;

/** 内部服务绑定名称 */
export const SERVICE_BINDING = {
  /** api-worker 绑定 verify-worker 的名称 */
  VERIFY: 'VERIFY',
  /** verify-worker 绑定 api-worker 的名称 */
  API: 'API',
  /** Pages 绑定 api-worker 的名称 */
  PAGES_API: 'API',
  /** Pages 绑定 verify-worker 的名称 */
  PAGES_VERIFY: 'VERIFY',
} as const;

/** 默认分页参数 */
export const DEFAULT_PAGE = {
  PAGE: 1,
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

/** 默认限流 */
export const DEFAULT_RATE_LIMIT = 100;

/** 默认心跳参数（秒） */
export const DEFAULT_HEARTBEAT = {
  INTERVAL: 60,
  TIMEOUT: 180,
} as const;

/** 密码长度限制 */
export const PASSWORD_LIMIT = {
  MIN: 6,
  MAX: 64,
} as const;

/** 用户名长度限制 */
export const USERNAME_LIMIT = {
  MIN: 6,
  MAX: 16,
} as const;

/** 机器码长度限制 */
export const MACHINE_CODE_LIMIT = {
  MAX: 32,
} as const;

/** 频率限制窗口（秒） */
export const RATELIMIT_WINDOW = 60;

/** 内部调用时间戳容差（秒） */
export const INTERNAL_TIMESTAMP_TOLERANCE = 60;
